import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plane, Thermometer, Plus, X, CalendarDays, Loader2, Check } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../lib/utils';

const API = process.env.REACT_APP_BACKEND_URL;

const TYPES = [
  { key: 'annual', label: 'Annual Leave' },
  { key: 'sick', label: 'Sick Leave' },
  { key: 'personal', label: "Personal / Carer's" },
  { key: 'unpaid', label: 'Unpaid Leave' },
  { key: 'other', label: 'Other' },
];
const STATUS = {
  pending: 'text-amber-700 bg-amber-50 border-amber-200',
  approved: 'text-emerald-700 bg-emerald-50 border-emerald-200',
  rejected: 'text-rose-700 bg-rose-50 border-rose-200',
  cancelled: 'text-slate-500 bg-slate-100 border-slate-200',
};
const fmtDate = (d) => new Date(d + 'T00:00:00').toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });

const LeaveSection = () => {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ type: 'annual', start_date: '', end_date: '', half_day: '', note: '' });

  const h = useCallback(() => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }), [token]);
  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/workforce/me/leave`, { headers: h() });
      const d = await r.json();
      setItems(d.requests || []);
    } catch { /* keep */ }
  }, [h]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.start_date) { toast.error('Pick a start date.'); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/workforce/me/leave`, { method: 'POST', headers: h(), body: JSON.stringify(form) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      toast.success('Leave request submitted for approval.');
      setOpen(false); setForm({ type: 'annual', start_date: '', end_date: '', half_day: '', note: '' });
      load();
    } catch (e) { toast.error(typeof e.message === 'string' ? e.message : 'Could not submit request.'); } finally { setBusy(false); }
  };

  const cancel = async (id) => {
    try { await fetch(`${API}/api/workforce/me/leave/${id}/cancel`, { method: 'POST', headers: h() }); load(); toast.message('Request cancelled.'); }
    catch { toast.error('Could not cancel.'); }
  };

  const singleDay = !form.end_date || form.end_date === form.start_date;

  return (
    <section className="rounded-xl border border-slate-200 bg-white" data-testid="leave-section">
      <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-slate-100">
        <h2 className="text-[15px] font-semibold">Time Off</h2>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setOpen((v) => !v)}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-[12.5px] font-semibold text-white bg-[#3A6AFF] hover:bg-[#2f59e0] transition-colors"
            data-testid="leave-request-toggle">
            {open ? <X className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />} Request time off
          </button>
        </div>
      </div>

      {open && (
        <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/50 space-y-3" data-testid="leave-form">
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Type</label>
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {TYPES.map((t) => (
                <button key={t.key} type="button" onClick={() => setForm((f) => ({ ...f, type: t.key }))}
                  className={cn('h-8 px-3 rounded-lg text-[12.5px] font-medium border transition-colors',
                    form.type === t.key ? 'bg-[#3A6AFF] text-white border-[#3A6AFF]' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300')}
                  data-testid={`leave-type-${t.key}`}>{t.label}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">From</label>
              <input type="date" value={form.start_date} onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))}
                className="mt-1.5 w-full h-9 px-3 rounded-lg border border-slate-200 text-[13px] bg-white" data-testid="leave-start" />
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">To <span className="text-slate-300">(optional)</span></label>
              <input type="date" value={form.end_date} min={form.start_date} onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))}
                className="mt-1.5 w-full h-9 px-3 rounded-lg border border-slate-200 text-[13px] bg-white" data-testid="leave-end" />
            </div>
          </div>
          {singleDay && (
            <div className="flex items-center gap-1.5">
              <span className="text-[12px] text-slate-500 mr-1">Half day?</span>
              {[['', 'Full'], ['am', 'Morning'], ['pm', 'Afternoon']].map(([v, l]) => (
                <button key={l} type="button" onClick={() => setForm((f) => ({ ...f, half_day: v }))}
                  className={cn('h-7 px-2.5 rounded-md text-[12px] font-medium border transition-colors',
                    form.half_day === v ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200')}
                  data-testid={`leave-half-${v || 'full'}`}>{l}</button>
              ))}
            </div>
          )}
          <textarea value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} rows={2}
            placeholder="Add a note for your manager (optional)" className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[13px] bg-white resize-none" data-testid="leave-note" />
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setOpen(false)} className="h-9 px-4 rounded-lg text-[13px] font-semibold text-slate-600 hover:bg-slate-100">Cancel</button>
            <button type="button" onClick={submit} disabled={busy}
              className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg text-[13px] font-semibold text-white bg-[#3A6AFF] hover:bg-[#2f59e0] disabled:opacity-60" data-testid="leave-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Submit request
            </button>
          </div>
        </div>
      )}

      <div className="p-5 space-y-2.5">
        {items.length === 0 && <p className="text-[13px] text-slate-400">No time-off requests yet.</p>}
        {items.map((r) => (
          <div key={r.id} className="flex items-center gap-3 rounded-lg border border-slate-100 px-3.5 py-2.5" data-testid={`leave-row-${r.id}`}>
            <span className={cn('w-8 h-8 rounded-lg flex items-center justify-center shrink-0', r.type === 'sick' ? 'text-rose-600 bg-rose-50' : 'text-[#3A6AFF] bg-blue-50')}>
              {r.type === 'sick' ? <Thermometer className="w-4 h-4" /> : <Plane className="w-4 h-4" />}
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[13px] font-semibold text-slate-800">{r.type_label}
                <span className="font-normal text-slate-500"> · {fmtDate(r.start_date)}{r.end_date !== r.start_date ? ` – ${fmtDate(r.end_date)}` : ''}{r.half_day ? ` (${r.half_day === 'am' ? 'morning' : 'afternoon'})` : ''}</span>
              </p>
              {(r.note || r.review_note) && <p className="text-[11.5px] text-slate-400 truncate">{r.review_note ? `Manager: ${r.review_note}` : r.note}</p>}
            </div>
            <span className={cn('text-[11px] font-semibold px-2 py-0.5 rounded-full border capitalize shrink-0', STATUS[r.status])} data-testid={`leave-status-${r.id}`}>{r.status}</span>
            {(r.status === 'pending' || r.status === 'approved') && (
              <button type="button" onClick={() => cancel(r.id)} className="text-slate-300 hover:text-rose-500 shrink-0" data-testid={`leave-cancel-${r.id}`} title="Cancel"><X className="w-4 h-4" /></button>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};

export default LeaveSection;
